import React from 'react';
import { Button } from '../ui/button';
import { Reveal } from '../motion/Reveal';
import HeroArt from './HeroArt';
import { ArrowRight, ShieldCheck, BadgeCheck, Landmark } from 'lucide-react';

const TRUST = [
    { icon: ShieldCheck, label: 'Evidence-based' },
    { icon: BadgeCheck, label: 'Reviewed by traffic police' },
    { icon: Landmark, label: 'Forwarded to authorities' },
];

/**
 * Marketing hero — mission-led, with the primary "Report a Violation" CTA.
 * @param {{ onReport: () => void, onLearn: () => void }} props
 */
export default function Hero({ onReport, onLearn }) {
    return (
        <section className="relative overflow-hidden bg-grad-sky" aria-labelledby="hero-title">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
                    {/* Copy */}
                    <Reveal stagger className="space-y-6">
                        <Reveal.Item>
                            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-sm font-medium">
                                Citizen-powered road safety
                            </span>
                        </Reveal.Item>
                        <Reveal.Item as="h1">
                            <span
                                id="hero-title"
                                className="block font-heading font-extrabold tracking-tight leading-[1.05] text-foreground"
                                style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
                            >
                                Safer roads start with{' '}
                                <span className="text-primary">responsible citizens.</span>
                            </span>
                        </Reveal.Item>
                        <Reveal.Item as="p" className="text-lg text-muted-foreground max-w-xl leading-relaxed">
                            Report traffic violations, promote road safety, and help build a more disciplined
                            society. Following the rules isn’t just lawful — it’s a mark of a thoughtful citizen.
                        </Reveal.Item>

                        <Reveal.Item className="flex flex-col sm:flex-row gap-3">
                            <Button size="lg" className="text-base h-12 px-6" onClick={onReport}>
                                Report a Violation
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                            <Button size="lg" variant="outline" className="text-base h-12 px-6" onClick={onLearn}>
                                Learn Road Safety
                            </Button>
                        </Reveal.Item>

                        <Reveal.Item className="flex flex-wrap gap-x-5 gap-y-2 pt-2">
                            {TRUST.map(({ icon: Icon, label }) => (
                                <span key={label} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                                    <Icon className="w-4 h-4 text-success" aria-hidden="true" />
                                    {label}
                                </span>
                            ))}
                        </Reveal.Item>
                    </Reveal>

                    {/* Art */}
                    <Reveal delay={0.1}>
                        <HeroArt />
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
